import {useCallback, useEffect, useSyncExternalStore} from "react";
import type {OtsColorScheme} from "./OtsTheme";

/** What a person asked for: follow the operating system, or always light or dark. */
export type OtsColorSchemeChoice = "system" | OtsColorScheme;

const KEY = "ots.color-scheme";
const CHANGED = "ots:color-scheme";

const readChoice = (): OtsColorSchemeChoice => {
  try {
    const stored = localStorage.getItem(KEY);
    return stored === "light" || stored === "dark" ? stored : "system";
  } catch {
    return "system";
  }
};

const systemPrefersDark = () => typeof window !== "undefined" && typeof window.matchMedia === "function"
  && window.matchMedia("(prefers-color-scheme: dark)").matches;

const snapshot = () => `${readChoice()}|${systemPrefersDark() ? "dark" : "light"}`;

const subscribe = (onChange: () => void) => {
  const media = typeof window.matchMedia === "function" ? window.matchMedia("(prefers-color-scheme: dark)") : undefined;
  window.addEventListener(CHANGED, onChange);
  window.addEventListener("storage", onChange);
  media?.addEventListener?.("change", onChange);
  return () => {
    window.removeEventListener(CHANGED, onChange);
    window.removeEventListener("storage", onChange);
    media?.removeEventListener?.("change", onChange);
  };
};

export type OtsColorSchemePreferenceResult = {
  /** What the person chose. */
  choice: OtsColorSchemeChoice;
  /** What that comes to right now. */
  colorScheme: OtsColorScheme;
  setChoice: (choice: OtsColorSchemeChoice) => void;
};

/**
 * An application's light/dark choice, remembered per browser and following the operating system until
 * someone picks. It stamps the result on `<html>` as `data-theme` (which `OtsThemeRoot`'s "system" scheme
 * and a Tailwind `dark` variant keyed on it both read) and as `data-ag-theme-mode` for AG Grid.
 */
export const useOtsColorSchemePreference = (): OtsColorSchemePreferenceResult => {
  const [choice, system] = useSyncExternalStore(subscribe, snapshot, () => "system|light").split("|") as [OtsColorSchemeChoice, OtsColorScheme];
  const colorScheme = choice === "system" ? system : choice;

  useEffect(() => {
    document.documentElement.dataset["theme"] = colorScheme;
    document.documentElement.dataset["agThemeMode"] = colorScheme;
    document.documentElement.style.colorScheme = colorScheme;
  }, [colorScheme]);

  const setChoice = useCallback((next: OtsColorSchemeChoice) => {
    try {
      if (next === "system") localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, next);
    } catch {
      // Private browsing: the choice lasts until the page reloads.
    }
    window.dispatchEvent(new Event(CHANGED));
  }, []);

  return {choice, colorScheme, setChoice};
};
